/**
 * Plantillas de notas de v1 (SDD SaaS §9.5) y sus esquemas de salida.
 *
 * El esquema que se entrega al modelo (salidas estructuradas) es exactamente
 * el de aquí; la API valida además evidencias y responsables en código
 * (SDD §9.4). Los campos de sistema (modelo, versión, feedback) NO forman
 * parte de estos esquemas: viven en `Summary`.
 */
import { z } from 'zod'
import type { TemplateId } from './types'

export const evidenceSchema = z.object({
  inicioMs: z.number().int().min(0),
  /** Cita textual breve (≤ 20 palabras; se valida en código, no en el esquema). */
  cita: z.string()
})

export const actionItemSchema = z.object({
  descripcion: z.string(),
  /** 'Yo' | 'Participante N' | null. Se valida contra los hablantes reales. */
  responsableEtiqueta: z.string().nullable(),
  /** Nombre dicho textualmente en la reunión, o null. */
  responsableMencionado: z.string().nullable(),
  /** Fecha o plazo tal como se dijo, resuelto a ISO cuando sea posible; o null. */
  fecha: z.string().nullable(),
  evidencia: z.array(evidenceSchema)
})

const itemConEvidencia = z.object({
  texto: z.string(),
  evidencia: z.array(evidenceSchema)
})

/** Núcleo común a todas las plantillas. */
export const notesBaseSchema = z.object({
  resumen: z.string(),
  puntosClave: z.array(z.string()),
  actionItems: z.array(actionItemSchema)
})

export const reunionGeneralSchema = notesBaseSchema.extend({
  temas: z.array(z.string()),
  decisiones: z.array(itemConEvidencia),
  preguntasAbiertas: z.array(z.string())
})

export const entrevistaSchema = notesBaseSchema.extend({
  /** 'si' | 'no' | 'no_consta' según lo que se oye en la conversación. */
  consentimientoConfirmado: z.enum(['si', 'no', 'no_consta']),
  perfilResumen: z.string(),
  fortalezas: z.array(itemConEvidencia),
  /** Solo sobre requisitos del cargo, con evidencia obligatoria. */
  puntosAVerificar: z.array(itemConEvidencia),
  siguientesPasos: z.array(z.string())
})

export const reunionClienteSchema = notesBaseSchema.extend({
  contexto: z.string(),
  necesidades: z.array(itemConEvidencia),
  compromisos: z.array(itemConEvidencia),
  riesgos: z.array(itemConEvidencia)
})

/**
 * Plantilla personalizada: núcleo común + una lista con evidencia por cada
 * sección definida por el usuario (`seccion_1` … `seccion_N`). El contenido
 * guarda además `_plantilla` (nombre y títulos al momento de generar), así las
 * notas se leen igual aunque después se edite o borre la plantilla.
 */
export const CUSTOM_SECTION_MAX = 8
export const customSectionKey = (i: number) => `seccion_${i + 1}`
export const personalizadaSchema = notesBaseSchema.passthrough()

/** Esquema de salida para el modelo, con una propiedad descrita por sección. */
export function customOutputSchema(secciones: { titulo: string; instruccion: string }[]) {
  const shape: Record<string, z.ZodType> = {}
  secciones.forEach((s, i) => {
    shape[customSectionKey(i)] = z
      .array(itemConEvidencia)
      .describe(`${s.titulo}${s.instruccion ? `: ${s.instruccion}` : ''}`)
  })
  return notesBaseSchema.extend(shape)
}

export type NotesBase = z.infer<typeof notesBaseSchema>
export type ReunionGeneralNotes = z.infer<typeof reunionGeneralSchema>
export type EntrevistaNotes = z.infer<typeof entrevistaSchema>
export type ReunionClienteNotes = z.infer<typeof reunionClienteSchema>
export type PersonalizadaNotes = z.infer<typeof personalizadaSchema>
export type Notes = ReunionGeneralNotes | EntrevistaNotes | ReunionClienteNotes | PersonalizadaNotes

export interface TemplateDefinition {
  id: TemplateId
  nombre: string
  descripcion: string
  schema: z.ZodType
  /** Límites que se piden por instrucción (el esquema no soporta maxItems). */
  limites: { puntosClave: number; actionItems: number; listas: number }
}

export const TEMPLATES: Record<TemplateId, TemplateDefinition> = {
  reunion_general: {
    id: 'reunion_general',
    nombre: 'Reunión general',
    descripcion: 'Temas, decisiones, tareas y preguntas abiertas de cualquier reunión.',
    schema: reunionGeneralSchema,
    limites: { puntosClave: 8, actionItems: 12, listas: 8 }
  },
  entrevista: {
    id: 'entrevista',
    nombre: 'Entrevista',
    descripcion:
      'Perfil, fortalezas y puntos a verificar sobre los requisitos del cargo. Sin inferencias sobre datos sensibles ni recomendación de contratar.',
    schema: entrevistaSchema,
    limites: { puntosClave: 8, actionItems: 8, listas: 6 }
  },
  reunion_cliente: {
    id: 'reunion_cliente',
    nombre: 'Reunión con cliente',
    descripcion: 'Contexto, necesidades, compromisos y riesgos de una reunión con un cliente.',
    schema: reunionClienteSchema,
    limites: { puntosClave: 8, actionItems: 12, listas: 8 }
  },
  personalizada: {
    id: 'personalizada',
    nombre: 'Personalizada',
    descripcion: 'Secciones definidas por ti.',
    schema: personalizadaSchema,
    limites: { puntosClave: 8, actionItems: 12, listas: 8 }
  }
}

/** Plantillas de eco que se ofrecen para elegir (la personalizada va aparte). */
export const STANDARD_TEMPLATE_IDS = ['reunion_general', 'entrevista', 'reunion_cliente'] as const

export const DEFAULT_TEMPLATE_ID: TemplateId = 'reunion_general'

export function parseNotes(templateId: TemplateId, contenido: unknown): Notes {
  return TEMPLATES[templateId].schema.parse(contenido) as Notes
}
